import { createContext, useContext, useReducer, useMemo } from 'react';
import type { ReactNode, Dispatch } from 'react';

import { wizardReducer, wizardInitialState } from './OnboardingWizardReducer';
import type { WizardState, WizardAction } from './OnboardingWizardReducer';

type OnboardingWizardContextValue = {
  state: WizardState;
  dispatch: Dispatch<WizardAction>;
};

const OnboardingWizardContext = createContext<OnboardingWizardContextValue | null>(null);

/**
 * Provider for the onboarding wizard state.
 * Wraps the wizard page and provides state + dispatch via context.
 */
export const OnboardingWizardProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(wizardReducer, wizardInitialState);
  const contextValue = useMemo(() => ({ state, dispatch }), [state, dispatch]);

  return <OnboardingWizardContext.Provider value={contextValue}>{children}</OnboardingWizardContext.Provider>;
};

/**
 * Hook to access the onboarding wizard context.
 *
 * @returns Wizard state and dispatch
 * @throws Error if used outside OnboardingWizardProvider
 */
export const useOnboardingWizardContext = () => {
  const context = useContext(OnboardingWizardContext);
  if (!context) {
    throw new Error('useOnboardingWizardContext must be used within OnboardingWizardProvider');
  }
  return context;
};
